import { domainExceptionList } from "@/services/variables";
import storage from "@/services/storage";
import moment from "moment";
import config from "@/config";
import { cleanedPhoneNumber } from "./api-data-mapping";
import { time } from "@/modules/crm/global/constants";
import momentTz from "moment-timezone";
import { findIana, findWindows } from "windows-iana";
import RootHub from "./signal-R/root-hubs";
import Report_Dashboard_signalr from "@/modules/crm/analytics/report-dashboard-signalr-constants";
import { FeatureKeys, Enums, WidgetDurationEnums } from "./dropdown-enums";
import FeatureService from "./feature-service";

/**
 * @name @queryStringToJSONObject
 * @description convert query string  to a json object
 * @requires a valid query string withwith a question mark at start
 * @returns a json object
 * */
export const queryStringToJSONObject = (query) => {
  let qs = decodeURI(query.substring(1))
    .replace(/"/g, '\\"')
    .replace(/&/g, '","')
    .replace(/=/g, '":"');
  return JSON.parse('{"' + qs + '"}');
};

/** Undo HTML entity encoding of `&` (email/SMS editors often emit `&amp;` in URLs). */
export const decodeAmpInQuery = (value = "") => {
  let next = String(value);
  for (let i = 0; i < 3; i += 1) {
    if (!/&amp;/i.test(next)) break;
    next = next.replace(/&amp;/gi, "&");
  }
  return next;
};

export const getPrefillSearchParams = () => {
  if (typeof window === "undefined") return new URLSearchParams();
  const raw = decodeAmpInQuery(
    (window.location.search || "").replace(/^\?/, "").replace(/\?/g, "&")
  );
  return new URLSearchParams(raw);
};

/**
 * @name @getDomain
 * @description get domain of the current URL and remove if www is there
 * @requires Null
 * @returns domain name without www
 * */
export const getDomain = () => {
  const testDomain = process.env.REACT_APP_TEST_APIURL;
  let location = window.location;
  let hostname = location.hostname;
  if (hostname?.split(".")[0] === "www") {
    hostname = hostname.replace("www.", "");
  }
  let domain = `${location.protocol}//${hostname}/`;
  //check if the domain is in exception list or not and
  //if yes then assign test domain else assign the current location domain
  return replaceTestDomain(domain) ? testDomain : domain;
};

/**
 * @name @replaceTestDomain
 * @description check if array defined in variable.js has same domain as the parameter passed from getDomain
 * @requires domain in string from getDomain.
 * @returns boolean
 * */
export const replaceTestDomain = (domain) => {
  let hasSameDomain = domainExceptionList.find((expectionDomain) => {
    return expectionDomain === domain;
  });
  return !!hasSameDomain;
};

/**
 * @name @setErrorClass
 * @description adding an error class on input field if its empty
 * @requires Two arguments required
 *               first as array [] which must have FormIK form object at postion 0
 *               (postion 1 is optional and must be a Boolian)
 *               second is the field name
 * @returns class name conditionally
 *              "has-value" if the input is not empty
 *              "is-invalid" if the field is not validated
 * */
export const setErrorClass = (payload, field) => {
  if (payload[0]?.values[field]) {
    return "has-value";
  } else if (payload[0]?.touched[field] || payload[1]) {
    return "is-invalid";
  } else {
    return "";
  }
};

/**
 * @name @matchDynamicRoute
 * @description match the dynammic path with the current page path
 * @requires Current defined route object
 * @returns Boolean (True if matched)
 * */
// TODO: commented out, not using anywhere
// export const matchDynamicRoute = route => {
//   const pathname = window.location.pathname;
//   // match the path and extract dynmaic id params
//   let matchRoutePathObj = matchPath(pathname, {
//     path: route.path
//   });
//   if (matchRoutePathObj) {
//     return replaceParamsId(route.path, matchRoutePathObj) || false;
//   }
//   return false;
// };
export const removeElementFromArray = (array, property, matchedCondition) => {
  //check allTrackings either facebook is there already?
  const index = array.findIndex((x) => x[property] === matchedCondition);
  let newArray = [];
  if (index === -1) {
    // if not found, do nothing..
    return array;
  } else {
    array.forEach((element, i) => {
      if (i !== index) {
        newArray.push(element);
      }
    });
    return newArray;
  }
};
export const setupTrackingPayload = (
  companyId,
  check,
  trackings,
  matchedCondition
) => {
  let allTrackings = Object.assign([], trackings);
  if (check) {
    // before push check the element is not exist.
    allTrackings = removeElementFromArray(
      allTrackings,
      "trackingType",
      matchedCondition
    );
    // then push
    allTrackings.push({
      companyId: companyId,
      trackingType: matchedCondition,
    });
  } else {
    allTrackings = removeElementFromArray(
      allTrackings,
      "trackingType",
      matchedCondition
    );
  }
  return allTrackings;
};
export const replaceParamsId = (routeString, matchRoutePathObj) => {
  const id = process.env.REACT_APP_DYNAMIC_ROUTE_ID;
  // split and remove empty string
  let splittedRoute = routeString?.split("/").filter((e) => e !== "");
  if (matchRoutePathObj) {
    // replacing :id to dynamic id
    splittedRoute.forEach((e, i) => {
      if (e === ":" + id) {
        splittedRoute[i] = matchRoutePathObj.params[id];
      }
    });
    // form a valid path to match
    return "/" + splittedRoute.join("/");
  }
};

/**
 * @name @mapServerValuesToInitialValues
 * @description  map existing server data to initial values
 *               A promise that resolves only when there
 *               is atleast a value on server
 *               which is in initial values object
 * @requires Two arguments required
 *               first server values object
 *               second initial values object
 * @returns Null
 * */

export const mapServerValuesToInitialValues = (
  serverValuesObject,
  initialValuesObject
) => {
  let hasData = serverValuesObject && Object.keys(serverValuesObject).length;
  return new Promise((resolve) => {
    let values = {};
    if (hasData) {
      for (let key in initialValuesObject) {
        if (serverValuesObject[key]) {
          values[key] = serverValuesObject[key];
        }
      }
    }
    if (Object.keys(values).length) resolve(values);
  });
};

/**
 * @name @camelCaseStringToNormalText
 * @description convert camel case string to normal to =>
 *              uppercase, lowercase or senetence case
 * @requires Required first argument string that needs to be converted
 *              optional second argument is case type
 * @returns converted string
 * */

export const formatPhoneNumber = (countryCode, number, mask) => {
  const digits = number.replace(/\D/g, "");
  let formattedNumber = countryCode + " ";

  let digitIndex = 0;
  for (let i = 0; i < mask.length; i++) {
    const maskChar = mask[i];
    formattedNumber += maskChar === "9" ? digits[digitIndex++] || "" : maskChar;
  }

  return formattedNumber;
};

export const isValidPhoneNumber = (
  phone,
  countryISOCode,
  countries,
  minLength = 8
) => {
  const selectedCountry = (countries?.countries || []).find(
    (country) => country.code === countryISOCode
  );

  if (!selectedCountry) return false;

  const cleanedPhone = cleanedPhoneNumber(phone || "");

  if (!cleanedPhone) return true; // allow empty phone

  const mask = selectedCountry?.phone_mask;

  if (mask) {
    // Convert mask to regex pattern
    const regexPattern = cleanedPhoneNumber(mask)
      .replace(/[\-\[\]\/\{\}\(\)\*\+\?\.\\\^\$\|]/g, "\\$&") // escape
      .replace(/9/g, "\\d"); // digits only

    const regex = new RegExp(`^${regexPattern}$`);
    return regex.test(cleanedPhone);
  }

  // fallback: min length check
  return cleanedPhone.length >= minLength;
};

export const camelCaseStringToNormalText = (
  string = "",
  letterCase,
  removeSpacesBtw = false
) => {
  let regex = removeSpacesBtw ? "$1" : " $1";
  let normalText = (string || "")
    .replace(/([A-Z])/g, regex)
    .trim()
    .replace(/^./, (text) => {
      return text;
    });
  if (letterCase === "uppercase") {
    return normalText.toUpperCase();
  }
  if (letterCase === "sentence") {
    let lowercase = normalText.toLowerCase().slice(1);
    return normalText.charAt(0).toUpperCase() + lowercase;
  }
  if (letterCase === "heading") {
    let wordsArray = normalText?.split(" ");
    let sentence = "";
    wordsArray.forEach((word) => {
      if (word.toLowerCase() === "id") {
        sentence +=
          string.toLowerCase() === "id" ? word.toUpperCase() + " " : " ";
      } else {
        sentence += (word[0]?.toUpperCase() + word.slice(1) + " ").replace(
          "undefined",
          ""
        );
      }
    });
    return sentence;
  }
  return normalText;
};

/**
 * @param {string|object} takes input and convert first char of string|object key to lowecase
 * @returns converted input
 */
export const convertFirstChar = (input, toLowerCase = true) => {
  if (typeof input === "string") {
    if (input.length > 0) {
      // Convert the first character of the string to lowercase or capitalize it
      return toLowerCase
        ? input.charAt(0).toLowerCase() + input.slice(1)
        : input.charAt(0).toUpperCase() + input.slice(1);
    } else {
      return input;
    }
  } else if (typeof input === "object" && input !== null) {
    // Convert the first character of each object key to lowercase or capitalize it
    const newObj = {};
    for (const key in input) {
      if (Object.prototype.hasOwnProperty.call(input, key)) {
        const newKey = toLowerCase
          ? key.charAt(0).toLowerCase() + key.slice(1)
          : key.charAt(0).toUpperCase() + key.slice(1);
        newObj[newKey] = input[key];
      }
    }
    return newObj;
  } else {
    // Handle other cases (e.g., unsupported types)
    return input;
  }
};

export const titleCaseToCamelCase = (key) => {
  return key?.charAt(0)?.toLowerCase() + key?.slice(1);
};

/**
 * @param {string} str takes string like 'This is test' and returns 'thisIsTest'
 * @returns converted camel case string
 */
export const camelize = (str) => {
  return str
    ?.replace(/(?:^\w|[A-Z]|\b\w)/g, function (word, index) {
      return index === 0 ? word.toLowerCase() : word.toUpperCase();
    })
    .replace(/\s+/g, "");
};

export const makeTitleFromKey = (key) =>
  key ? key.match(/[A-Z][a-z]+/g)?.join(" ") : "";

/**
 * @name @convertToNonMeridianTime
 * @description takes time in 24 hour e.g: "11:00 PM" and converts into "23:00"
 * @requires time12h in string "11:00 PM"
 * @returns string "23:00"
 * */
export const convertToNonMeridianTime = (time12h) => {
  const [time, modifier] = time12h?.split(" ");
  let [hours, minutes] = time?.split(":");
  if (hours === "12") {
    hours = "00";
  }
  if (modifier && modifier?.split(" ")[0] === "PM") {
    hours = parseInt(hours, 10) + 12;
  }
  return `${hours}:${minutes}`;
};

/**
 * @name @convertToMeridianTime
 * @description takes time in 24 hour e.g: "23:00" and converts into "11:00 PM"
 * @requires time12h in string "23:00"
 * @returns string "11:00 PM"
 * */
export const convertToMeridianTime = (time24h) => {
  let timeSplit = time24h?.split(":"),
    hours,
    minutes,
    meridian;
  hours = timeSplit[0];
  minutes = timeSplit[1];
  if (hours > 12) {
    meridian = "PM";
    hours -= 12;
  } else if (hours < 12) {
    meridian = "AM";
    if (hours === 0) {
      hours = 12;
    }
  } else {
    meridian = "PM";
  }
  return hours + ":" + minutes + " " + meridian;
};

/**
 * String Substitution is same as ECMA6 Substitution,
 * Usage: this.Name = 'Petter'; this.Join = 'join';
 * ApexChat.stringSubstitution('Hello @{Name}, you are requested to @{Join}', this); // "Hello John, you are requested to join"
 * @param {any} completeString
 * @param {any} obj
 */
export const stringSubstitution = (tempStr, obj) => {
  tempStr = tempStr.replace(/@{(.+?)}/g, "'$1'");
  let arr = tempStr?.split("'");
  for (let i = 0; i < arr.length; i++) {
    if (obj[arr[i]]) {
      arr[i] = obj[arr[i]];
    }
  }
  return arr.join("");
};

export const getEnumValue = (enumArray, value) => {
  return enumArray.filter((e) => e.value === value)[0]["name"];
};

export const removeKeysFromLS = (keys = []) => {
  // var head
  keys.forEach((element) => {
    storage.deleteItem(element);
  });
};
export const removeFromCookies = (keys = []) => {
  keys.forEach((element) => {
    setCookie(element, "", -3);
  });
};

const getDocument = () => {
  if (typeof document !== "undefined") return document;
  return {};
};

export const getDomainForCookie = () => {
  let domain = globalThis?.window?.location?.host.replace("smarthub", "");
  if (config.environment === "development") {
    domain = "";
  }
  return domain;
};

export const getCookie = (cname, cookies = null) => {
  let name = cname + "=";
  let decodedCookie = decodeURIComponent(cookies || getDocument()?.cookie);
  let ca = decodedCookie?.split(";");
  for (let i = 0; i < ca.length; i++) {
    let c = ca[i];
    while (c.charAt(0) === " ") {
      c = c.substring(1);
    }
    if (c.indexOf(name) === 0) {
      return c.substring(name.length, c.length);
    }
  }
  return "";
};

export const setCookie = (cname, cvalue, exdays = 1) => {
  let d = new Date();
  d.setTime(d.getTime() + exdays * 24 * 60 * 60 * 1000);
  let expires = "expires=" + d.toUTCString();
  const value = encodeURIComponent(cvalue);
  let cookie = cname + "=" + value + ";" + expires + ";path=/";
  let cdomain = getDomainForCookie();
  if (cdomain) cookie = `${cookie}; domain=${cdomain};`;
  getDocument().cookie = cookie;
};

export const setCookieForSpecificMinutes = (cname, cvalue, minutes = 1) => {
  let d = new Date();
  d.setTime(d.getTime() + minutes * 60 * 1000);
  let expires = "expires=" + d.toUTCString();
  getDocument().cookie = cname + "=" + cvalue + ";" + expires + ";path=/";
};
// A utility function as a better replacement for setTimeOut:
// keeps checking on a boolean condition passed as a 2nd arg
// and then only runs a func after the condition is found to be true
export const RunAsSoonAs = function (func, condition) {
  var interval = setInterval(function () {
    var conditionResult = condition();
    if (
      typeof condition === "function" &&
      typeof conditionResult === "boolean" &&
      conditionResult
    ) {
      func();
      clearInterval(interval);
    }
  }, 0);
};
export const loadScript = (src = "") => {
  let element = document.getElementById("chat_bot_id");
  if (!element) {
    let head = document.getElementsByTagName("head");
    let script = document.createElement("script");
    script.type = "text/javascript";
    script.src = src;
    script.id = "chat_bot_id";
    head[0].appendChild(script);
  }
};
export const dateTimerFormat = (date) => {
  return moment(new Date(date)).format("MM/DD/YYYY hh:mm:ss a");
};

export const formatTime = (date, format = "hh:mm a") => {
  if (!date) {
    return "";
  }
  return moment(date).format(format);
};

/** Appointment APIs store local wall-clock times, often with a trailing Z. */
export const parseAppointmentWallClock = (date) => {
  const stripped = String(date || "")
    .replace(/\.\d{3}Z$/i, "")
    .replace(/Z$/i, "");
  const wallClock = moment.utc(stripped);
  return wallClock.isValid() ? wallClock : moment.utc(date);
};

export const isAppointmentUpcoming = (date) => {
  if (!date) return false;
  const startWall = parseAppointmentWallClock(date);
  const nowWall = moment.utc(moment().format("YYYY-MM-DDTHH:mm:ss"));
  return startWall.isAfter(nowWall);
};

export const dayAgo = (date) => {
  return moment
    .utc(date)
    .local()
    .fromNow()
    .replace("a few seconds", " 1s")
    .replace(" minutes", " min")
    .replace(" minute", " min")
    .replace(" hours", " hr")
    .replace(" hour", " hr");
};

export const calculateTimeDifferenceBetweenChat = (
  firstMsgCreatedOn,
  selectedChatCreatedOn,
  format = ""
) => {
  if (!firstMsgCreatedOn || !selectedChatCreatedOn) {
    return "";
  }
  let _firstMsg = moment(firstMsgCreatedOn);
  let _selectedChatCreatedOn = moment(selectedChatCreatedOn);
  let duration = moment.duration(_firstMsg.diff(_selectedChatCreatedOn));
  let inSeconds = parseInt(duration.asSeconds());
  if (format && inSeconds >= 0) {
    return format.replace("format", inSeconds);
  }
  return inSeconds;
};

export const parseFromJSON = (data) => {
  if (data && typeof data === "string") return JSON.parse(data);
  else return data;
};

export const abbrNum = (number, decPlaces) => {
  decPlaces = Math.pow(10, decPlaces);
  const abbrev = ["K", "M", "B", "T"];

  for (let i = abbrev.length - 1; i >= 0; i--) {
    let size = Math.pow(10, (i + 1) * 3);

    if (size <= number) {
      number = Math.round((number * decPlaces) / size) / decPlaces;

      number += abbrev[i];

      break;
    }
  }

  return number;
};

export const changeTourGuideFlag = () => {
  let loginUser = JSON.parse(storage.getItem("ViewedUser"));
  let profile = JSON.parse(storage.getItem("Profile"));
  if (loginUser) {
    const objIndex = loginUser.findIndex(
      (obj) => obj.userId === profile.UserID
    );
    loginUser[objIndex].isLogginBefore = true;
    storage.setItem("ViewedUser", loginUser);
  }
};

export const truncateText = (data, limit) => {
  let text = data;
  if (text) {
    text = text.toString();
    text = text.trim();
    if (text.length > limit) {
      text = text.substring(0, limit).trim();
      const brokenEmojiRegex = /(?:&#\d{0,5}|&)$/;
      // remove broken emoji if exists
      if (brokenEmojiRegex.test(text)) {
        text = text.replace(brokenEmojiRegex, "");
      }

      text += "...";
    }
  }
  return text;
};

// Note: not in use
// export const toolTipPlacement = window.innerWidth <= 1100 ? 'bottom' : 'left';

export const insertInArray = (arr, index, newItem) => [
  ...arr.slice(0, index),
  newItem,
  ...arr.slice(index),
];

export const getUserTimezone = () => {
  const today = new Date();
  const short = today.toLocaleDateString(undefined);
  const full = today.toLocaleDateString(undefined, { timeZoneName: "long" });

  // Trying to remove date from the string in a locale-agnostic way
  const shortIndex = full.indexOf(short);
  if (shortIndex >= 0) {
    const trimmed =
      full.substring(0, shortIndex) + full.substring(shortIndex + short.length);

    // by this time `trimmed` should be the timezone's name with some punctuation -
    // trim it from both sides
    return trimmed.replace(/^[\s,.\-:;]+|[\s,.\-:;]+$/g, "");
  } else {
    // in some magic case when short representation of date is not present in the long one, just return the long one as a fallback, since it should contain the timezone's name
    return full;
  }
};

export const addSubdomainInURL = (url, subDomain) => {
  return url.replace(
    /^(http|https):\/\//,
    (url.indexOf("https") === -1 ? "http" : "https") + "://" + subDomain + "."
  );
};

export const updateViewedUser = (value) => {
  let ViewedUsers = JSON.parse(storage.getItem("ViewedUser"));
  let profile = JSON.parse(storage.getItem("Profile"));
  if (ViewedUsers) {
    const objIndex = ViewedUsers.findIndex(
      (obj) => obj.userId === profile?.UserID
    );
    if (objIndex >= 0) {
      ViewedUsers[objIndex] = { ...ViewedUsers[objIndex], ...value };
    } else {
      let user = { userId: profile?.UserID, ...value };
      ViewedUsers.push(user);
    }
    storage.setItem("ViewedUser", ViewedUsers);
  } else {
    let user = { userId: profile?.UserID, ...value };
    storage.setItem("ViewedUser", [user]);
  }
};

export const getUserFromViewedUser = () => {
  let ViewedUsers = JSON.parse(storage.getItem("ViewedUser"));
  let profile = JSON.parse(storage.getItem("Profile"));
  let user = null;
  if (ViewedUsers) {
    const objIndex = ViewedUsers.findIndex(
      (obj) => obj.userId === profile?.UserID
    );
    if (objIndex >= 0) {
      user = ViewedUsers[objIndex];
    }
  }

  return user;
};

/**
 * @name @addPrefixToObjectKeys
 * @description add prefix to every key of the provided object
 * @requires Required first argument as object which keys to be updated
 *           prefix string to be added
 * @returns updated object
 * */

export const addPrefixToObjectKeys = (obj, prefix) => {
  let newObject = {};
  Object.keys(obj).forEach((key) => {
    newObject[prefix + key] = obj[key];
  });

  return newObject;
};

export const getEnumTextByValue = (enumObj, value) => {
  for (var k in enumObj) if (enumObj[k] === value) return k;
  return null;
};

export const jsonParser = (data) => {
  if (data) return JSON.parse(data);
  else return data;
};

export const checkAuthToken = () => {
  let isAuth = !!getCookie("CRM-Auth");
  return isAuth;
};

export const checkSmarthubUser = () => {
  const isSmarthubUser = !!getCookie("smarthub-user");
  return isSmarthubUser;
};

export const generateDurationOptions = (customOptions = []) => {
  let defaultOptions = { ...Enums.durations };
  if (customOptions.length)
    Object.keys(defaultOptions).forEach((key) => {
      if (!customOptions.includes(defaultOptions[key])) {
        delete defaultOptions[key];
      }
    });
  return defaultOptions;
};

// download file
// export const downloadFile = (file, fileName, format) => {
//   const url = window.URL.createObjectURL(
//     new Blob([file], { type: `application/${format}` })
//   );
//   const link = document.createElement("a");
//   link.href = url;
//   link.setAttribute("download", `${fileName}.${format}`); //or any other extension
//   document.body.appendChild(link);
//   link.click();
//   document.body.removeChild(link);
// };

const checkValue = (value) => {
  if (value === "") {
    return null;
  } else {
    return value;
  }
};

// Converte file JSON to CSV and download it
export const JSONToCSVFileConvertor = (JSONData, ReportTitle, colsDetail) => {
  // If JSONData is not an object then JSON.parse will parse the JSON string in an Object
  var arrData = typeof JSONData != "object" ? JSON.parse(JSONData) : JSONData;
  var CSV = "";
  // Set Report title in first row or line
  CSV += ReportTitle + "\r\n\n";
  // This  will generate the Label/Header
  let row = "";
  // This loop will extract the label from 1st index of on array
  if (arrData[0] && colsDetail.length > 0) {
    //checking JSON have data and columns include in export
    for (let i = 0; i < colsDetail.length; i++) {
      row += colsDetail[i].header + ",";
    }
  }
  // remove the last comma
  row = row.slice(0, -1);
  // append Label row with line break
  CSV += row + "\r\n";
  // 1st loop is to extract each row
  for (let i = 0; i < arrData.length; i++) {
    let row = "";
    // 2nd loop will extract each column and convert it in string comma-separated
    for (var index = 0; index < colsDetail.length; index++) {
      if (
        colsDetail[index].type === "date" ||
        colsDetail[index].filterType === "date"
      ) {
        var dateValue = arrData[i][colsDetail[index].name];
        if (dateValue) {
          var regexMatches = dateValue.match(dateRegexwithNegativesign);
          if (regexMatches.length > 1) {
            var dateTicks = regexMatches[1];
            if (parseInt(dateTicks, 10) < 0) {
              row += '"N/A",';
              continue;
            }
          }
        }
        row += '"' + longDateRenderer(dateValue) + '",';
      } else {
        let key = colsDetail[index].name;
        if (key === "id") {
          row += '"' + (arrData[i][key] || arrData[i]["Id"]) + '",';
        } else {
          row += '"' + checkValue(arrData[i][key]) + '",';
        }
      }
    }
    row.slice(0, row.length - 1);
    // add a line break after each row
    CSV += row + "\r\n";
  }

  if (CSV === "") {
    alert("Invalid data");
    return;
  }
  //this will remove the blank-spaces from the title and replace it with an underscore
  var fileName = ReportTitle.replace(/ /g, "_");
  //Initialize file format you want csv or xls
  var uri = "data:text/csv;charset=utf-8," + escape(CSV);
  // Now the little tricky part.
  // you can use either>> window.open(uri);
  // but this will not work in some browsers
  // or you will not get the correct file extension
  // this trick will generate a temp <a /> tag
  var link = document.createElement("a");
  link.href = uri;
  // set the visibility hidden so it will not effect on your web-layout
  link.style = "visibility:hidden";
  link.download = fileName + ".csv";
  // this part will append the anchor tag and remove it after automatic click
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

const dateRegexwithNegativesign = /\/Date\((-?\d+[\-|\+]?\d{0,4})\)\//;
const dateRegex = /\/Date\(-?(\d+[\-|\+]?\d{0,4})\)\//;

const longDateRenderer = (value) => {
  if (value && typeof value.getDate === "undefined") {
    value = parseDate(value);
  }
  if (value) {
    return moment(value).format("DD/MM/YY h:m:s ZZ");
  }
  return value === null ? null : value;
};

const parseDate = function (value) {
  if (!value) {
    return value;
  }
  if (typeof value.getDate !== "undefined") {
    return value;
  }
  var ticksAndZone = value.match(dateRegex)[1];
  var delim = ticksAndZone.indexOf("-") > -1 ? "-" : "+";
  var parts = ticksAndZone?.split(delim);
  var ticks = parseInt(parts[0], 10);
  return new Date(ticks);
};

export const isObjectEmpty = (obj) => {
  let isEmpty = Object.keys(obj).length ? false : true;
  return isEmpty;
};

export const RandomNumber = () => {
  return Date.now() + Math.floor(Math.random() * 100);
};
export const getApexchatOwnerKeys = config.apexchatOwnerCompanyKeys?.split(",");

export const getReachlocalOwnerKeys =
  config.hideSectionSettingsForOwnerCompanyKeys?.split(",");

export const getTimeDifference =
  (startTime = moment()) =>
  (endTime = moment()) => {
    let difference = endTime.diff(startTime, "seconds");
    return difference;
  };

export const isInViewport = (elem) => {
  const rect = elem.getBoundingClientRect();
  return (
    rect.top >= 0 &&
    rect.left >= 0 &&
    rect.bottom <=
      (window.innerHeight || document.documentElement.clientHeight) &&
    rect.right <= (window.innerWidth || document.documentElement.clientWidth)
  );
};

export const ToastTemplate = {
  success: (val) => getTemplate("Success", val),
  error: (val) => getTemplate("Error", val),
  info: (val) => getTemplate("Info", val),
  warning: (val) => getTemplate("Warning", val),
};
const getTemplate = (title, msg) => (
  <div style={{ lineHeight: "1.3" }}>
    <b>{title}</b>
    <div>{msg}</div>
  </div>
);

// create url after SSO redirection for specific page
export const createReturnUrl = (path = "") => {
  return path;
};

export const getQueryStringVariables = (path, key) => {
  return unescape(
    path.replace(
      new RegExp(
        "^(?:.*[&\\?]" +
          escape(key).replace(/[\.\+\*]/g, "\\$&") +
          "(?:\\=([^&]*))?)?.*$",
        "i"
      ),
      "$1"
    )
  );
};

export const getGroupByDate = (date) => {
  const today = moment().startOf("day");
  const dateMoment = moment.utc(date).local();
  const diffInDays = today.diff(dateMoment.startOf("day"), "days");

  if (diffInDays === 0) {
    return "Today";
  } else if (diffInDays === 1) {
    return "Yesterday";
  } else if (diffInDays >= 2 && diffInDays <= 7) {
    return "Earlier this Week";
  } else if (diffInDays >= 8 && diffInDays <= 14) {
    return "Last Week";
  } else {
    return "Other";
  }
};

export const convertCustomerPortalLinkToLegacyPortal = (
  location,
  queryParam
) => {
  let path = location.pathname + location?.search;
  let id = getQueryStringVariables(path, queryParam);
  // /portal/leads?id=123 => /Customer.aspx?leadId=123
  if (path.includes("lead")) {
    // SSO-81 leads not includes in path and leadId queryParams not available
    if (!id) {
      id = getQueryStringVariables(path, "leadId");
    }

    return `/Customer.aspx?leadId=${id}`;
  }
  // /portal/onboarding?id=123 => /portal.aspx#provisioningrequests/123
  if (path.includes("onboarding")) {
    return `/portal.aspx#provisioningrequests/${id}`;
  }
  // /portal/chats?id=123 => /portal.aspx?chatId=123
  if (path.includes("chats")) {
    return `/portal.aspx?chatId=${id}`;
  }

  return location?.hash
    ? `/portal.aspx${location?.hash}`
    : `/portal.aspx?${location?.search}`;
};

export const convertLegacyPortalLinkToCustomerPortal = (
  location,
  queryParam
) => {
  let path = location?.pathname + (location?.hash || location?.search);
  // /Customer.aspx?leadId=123 => /portal/leads?id=123
  if (path.toLowerCase().includes("customer.aspx?leadid")) {
    let id = getQueryStringVariables(path, "leadId");
    return `/portal/leads?id=${id}`;
  }
  // /portal.aspx#provisioningrequests/123 => /portal/onboarding?id=123
  if (path.includes("#provisioningrequests/")) {
    let id = path?.split("provisioningrequests/")[1];
    return `/portal/onboarding?id=${id}`;
  }
  // /portal.aspx?chatId=123 => /portal/chats?id=123
  if (path.includes("portal.aspx?chatId")) {
    let id = getQueryStringVariables(path, "chatId");
    return `/portal/chats?id=${id}`;
  }
  return path.includes("isCP=true") ? path : "/home";
};

export const generateOptions = (
  list = [],
  labelkey = "label",
  valueKey = "value",
  alternateLabel = "label",
  labelCase = null
) =>
  list?.map((item) => {
    let label = item?.[labelkey];
    if (alternateLabel && !label) {
      label = item?.[alternateLabel];
    }
    if (labelCase) {
      label = camelCaseStringToNormalText(label, labelCase);
    }
    return {
      ...item,
      label: label,
      value: item?.[valueKey],
    };
  });

export const generateOptionsDefault = (
  list = [],
  labelKey = "label",
  valueKey = "id"
) =>
  list.map((item) => ({
    label: item[labelKey] || item.label || item.name,
    id: item[valueKey] || item.id || item.value,
  }));

export const getNameInitials = (name) => {
  if (name) {
    let rgx = new RegExp(/(\p{L}{1})\p{L}+/, "gu");
    let initials = [...name.matchAll(rgx)] || [];
    return (
      (initials.shift()?.[1] || "") + (initials.pop()?.[1] || "")
    ).toUpperCase();
  }
};

export const isElementVisible = (el, holder) => {
  holder = holder || document.body;
  const { top, bottom, height } = el.getBoundingClientRect();
  const holderRect = holder.getBoundingClientRect();

  return top <= holderRect.top
    ? holderRect.top - top <= height
    : bottom - holderRect.bottom <= height;
};
export const getFullName = (data, keys = ["firstName", "lastName"]) => {
  const [firstName, lastName] = keys;
  return data?.[firstName] && data?.[lastName]
    ? `${data?.[firstName]} ${data?.[lastName]}`
    : data?.[firstName] || data?.[lastName] || "Anonymous";
};

export const getSimilarItemsFrom2Arrays = (
  firstArr,
  arrayKey1,
  secondArr,
  arrayKey2,
  valueType = "string"
) => {
  const tempArr = firstArr.filter((item1) => {
    return secondArr.some((item2) => {
      return valueType === "string"
        ? item2[arrayKey2].toLowerCase() === item1[arrayKey1].toLowerCase()
        : +item2[arrayKey2] === +item1[arrayKey1];
    });
  });

  return tempArr || [];
};

export const isArraySorted = (array, key) => {
  let isSorted = array.every(
    (obj, index) => index === 0 || obj[key] >= array[index - 1][key]
  );
  return isSorted;
};

export const sortArray = (array = [], keyToSort, sortDirection = "ASC") => {
  let sorted = [];
  if (Array.isArray(array) && array.length) {
    if (sortDirection === "ASC") {
      sorted = array.sort((a, b) => a[keyToSort] - b[keyToSort]);
    } else {
      sorted = array.sort((a, b) => b[keyToSort] - a[keyToSort]);
    }
  }
  return sorted;
};

export const sortByDisplayName = (users = []) => {
  if (!Array.isArray(users)) return users;
  return [...users].sort((a, b) => {
    const nameA = (a?.displayName || a?.username || "").toLowerCase();
    const nameB = (b?.displayName || b?.username || "").toLowerCase();
    return nameA.localeCompare(nameB);
  });
};

export const getParsedJson = (string) => {
  try {
    JSON.parse(string);
    return JSON.parse(string);
  } catch (error) {
    return string;
  }
};
export const getStringifyJson = (json) => {
  try {
    return JSON.stringify(json);
  } catch (error) {
    return json;
  }
};

//
export const cloneDeep = (input) => {
  try {
    return JSON.parse(JSON.stringify(input));
  } catch (error) {
    return input;
  }
};

export const serialize = (data) => (data ? cloneDeep(data) : data);

export const convertKeyValueArrayToObject = (arr) => {
  let obj = {};
  if (Array.isArray(arr)) {
    arr?.forEach((item) => {
      obj[titleCaseToCamelCase(item.key)] = item.value;
    });
  } else {
    obj = arr;
  }
  return obj;
};

export const isValidDate = (dateString) => {
  if (/^\d+$/.test(dateString)) {
    return false; // Input is just a number, not a valid date string for our purposes
  }
  const date = new Date(dateString);
  return date instanceof Date && !isNaN(date.getTime());
};

const GUID_PATTERN =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export const isGuid = (id) => GUID_PATTERN.test(String(id || "").trim());

const isUnixTimestamp = (value) => {
  const timestamp = Number(value);

  // Check if the value is a valid number and within the range of Unix timestamps
  return !isNaN(timestamp) && timestamp >= 0 && timestamp <= 2147483647000;
};

// third arguement isLocal will be passed as false
// in case of showing the serverside dates to avoid TZ UI issues
export const getFormattedDate = (date, format = null, isLocal = true) => {
  if (isValidDate(date) || isUnixTimestamp(date)) {
    let momentObj = isUnixTimestamp(date)
      ? moment.unix(date)
      : isLocal
        ? moment.utc(date)?.local()
        : moment.utc(date);
    if (!format) return momentObj.format("Do MMM YYYY [at] h:mm a");
    else return momentObj.format(format);
  } else {
    return date;
  }
};

export const getTaskCalendarDayKey = (date) => {
  if (!date) return "";
  return moment.utc(date).local().format("YYYY-MM-DD");
};
export const getUniqueFromArray = (data = []) =>
  data.filter((item, i, ar) => ar.indexOf(item) === i);

export const SortDir = {
  1: "ASC",
  "-1": "DESC",
};

const formats = [
  "DD MMM, YYYY",
  "hh:mm A - DD MMM, YYYY",
  time.format.fetchDate,
  time.format.date,
];

const clockFormats = [
  time.format.dateWithTime,
  "YYYY-MM-DD HH:mm:ss",
  "YYYY-MM-DD hh:mm A",
  "YYYY-MM-DD h:mm A",
  "hh:mm A",
  "h:mm A",
  "HH:mm",
];

const parseFilterClock = (value) => {
  if (value == null || value === "" || value === "Invalid date") {
    return null;
  }

  const hourFn = value?.hours || value?.hour;
  if (typeof hourFn === "function") {
    const minuteFn = value.minutes || value.minute;
    const built = moment({
      year: typeof value.year === "function" ? value.year() : moment().year(),
      month:
        typeof value.month === "function" ? value.month() : moment().month(),
      date: typeof value.date === "function" ? value.date() : moment().date(),
      hour: hourFn.call(value),
      minute: typeof minuteFn === "function" ? minuteFn.call(value) : 0,
      second: 0,
    });
    return built.isValid() ? built : null;
  }

  if (value instanceof Date) {
    const built = moment(value);
    return built.isValid() ? built : null;
  }

  const parsed = moment(String(value).trim(), clockFormats, true);
  return parsed.isValid() ? parsed : null;
};

export const generateGqlParams = (data, appendWhere = null) => {
  const {
    filterValue = [],
    sortInfo,
    skip,
    limit,
    where = {},
    defaultIdSort = "id",
    // defaultSortKey = "id",
  } = data;
  // const staticOperatorCols = ["salutation"];
  let payload = {
    where: null,
    order: null,
    page: skip / limit,
    pageSize: limit,
    skip: skip,
    take: limit,
  };
  let order = {};

  if (filterValue || filterValue == null) {
    let where = { and: [] };

    if (appendWhere) {
      where.and.push(...appendWhere);
    }
    filterValue &&
      filterValue.forEach((fv) => {
        if (fv.value || fv.value === false || fv.value === 0) {
          if (fv.type === "time" || fv.type == "date") {
            let startDate;
            let endDate;
            const timeValue = parseFilterClock(fv.value);
            const valueText =
              typeof fv.value === "string" ? fv.value.trim() : "";
            const isTimeFilter =
              fv.type === "time" ||
              /^\d{4}-\d{2}-\d{2}[ T]\d{2}:\d{2}/.test(valueText) ||
              /^\d{1,2}:\d{2}\s*[AP]M$/i.test(valueText);

            if (isTimeFilter) {
              // displayLocalTime - company offset (browser zone).
              // Pacific (UTC-7): 5:20 PM -> 00:00-00:59, which matches
              // a stored dateValue of 2026-10-09T00:20:00.000Z.
              if (!timeValue) {
                return;
              }
              const localStart = timeValue.clone().startOf("hour");
              const localEnd = localStart.clone().add(59, "minutes");
              startDate = moment.utc(localStart);
              endDate = moment.utc(localEnd);
            } else if (fv.name?.toLowerCase()?.includes("cdate")) {
              startDate = moment(fv.value, formats, true)?.startOf("day");
              endDate = moment(fv.value, formats, true)?.endOf("day");
            } else {
              startDate = moment.utc(
                moment(fv.value, formats, true).startOf("day")
              );
              endDate = moment.utc(
                moment(fv.value, formats, true).endOf("day")
              );
            }
            const gte = startDate?.isValid()
              ? startDate.format(time.format.dateWithTime)
              : "";
            const lte = endDate?.isValid()
              ? endDate.format(time.format.dateWithTime)
              : "";
            if (
              !gte ||
              !lte ||
              gte === "Invalid date" ||
              lte === "Invalid date"
            ) {
              return;
            }
            where["and"].push({
              [fv.name]: { gte },
            });
            where["and"].push({
              [fv.name]: { lte },
            });
          } else if (fv.name?.toLowerCase()?.includes("cmultivalue")) {
            let multiValue = {
              leadCustomMultipleValuesList: {
                some: {
                  leadCustomOptionsId: {
                    in: Array.isArray(fv.value) ? fv.value : [fv.value],
                  },
                },
              },
            };
            where["and"].push(multiValue);
          } else if (fv.name === "leadTags") {
            let key = fv.name === "leadTags" ? "tagId" : "id";
            where["and"].push({
              [fv.name]: {
                some: { [key]: { [fv.operator]: getValueByType(fv) } },
              },
            });
          } else if (fv.name === "leadAppointments") {
            where["and"].push({
              [fv.name]: {
                some: {
                  calendarId: { [fv.operator]: getValueByType(fv) },
                },
              },
            });
          } else if (fv.name === "businessCategoryIds") {
            where["and"].push({
              [fv.name]: {
                some: { in: [getValueByType(fv)] },
              },
            });
          } else {
            // if (staticOperatorCols.includes(fv.name)) fv.operator = "startsWith";
            if (fv.type === "number" && isNaN(getValueByType(fv))) {
            } else
              where["and"].push({
                [fv.name]: { [fv.operator]: getValueByType(fv) },
              });
          }
        }
      });
    if (Object.keys(where).length) payload.where = where;
  }

  if (Object.keys(where).length) payload.where = where;

  if (sortInfo) {
    order[sortInfo.name] = SortDir[sortInfo.dir];
  }

  payload.order = Object.keys(order).length
    ? [order]
    : defaultIdSort
      ? [{ [defaultIdSort]: "DESC" }]
      : [];

  return payload;
};

export const getValueByType = (data) => {
  switch (data.type) {
    case "number":
      return Number(data.value);
    case "date":
      return data.value ? moment(data.value).unix() : data.value;
    default:
      return data.value;
  }
};

export const returnURL = (newKeyword = "my") => {
  const hostDomain = JSON.parse(storage.getItem("Profile"))?.HostDomain;
  let originUrl = globalThis?.window?.location?.origin || "";
  let currentUrl = "";

  if (config.environment === "development") {
    currentUrl = config.customerPortalURL;
    return currentUrl;
  } else {
    currentUrl = originUrl.replace("smarthub.", "") + "/";
  }
  let url = (hostDomain || currentUrl).split("//").join(`//${newKeyword}.`);
  return url;
};

export const legacyURL = () => {
  const hostDomain = JSON.parse(storage.getItem("Profile"))?.HostDomain;
  let originUrl = globalThis?.window?.location?.origin || "";
  let currentUrl = "";

  if (config.environment === "development") {
    currentUrl = config.portalURL;
    return currentUrl;
  } else {
    currentUrl = originUrl.replace("smarthub.", "") + "/";
  }
  let url = hostDomain || currentUrl;
  return url;
};

export const downloadFile = (file, filename, src) => {
  const url = src ?? URL.createObjectURL(new Blob([file]));
  const link = document.createElement("a");
  link.href = url;
  link.setAttribute("download", filename);
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
};

export const generateRandom = (maxLimit = 100) => {
  let rand = Math.random() * maxLimit;
  // say 99.81321410836433
  rand = Math.floor(rand); // 99

  return rand;
};

export const generateImageUrlByName = (name = "") => {
  if (name && (name.startsWith("blob:") || name.startsWith("http"))) {
    return name;
  }
  const hostDomain = JSON.parse(storage.getItem("Profile"))?.HostDomain;
  let originUrl = globalThis?.window?.location?.origin || "";
  let currentUrl = "";

  if (config.environment === "development") {
    currentUrl = config.customerPortalURL;
    return currentUrl;
  } else {
    currentUrl = originUrl.replace("smarthub.", "") + "/";
  }
  return name
    ? `${
        hostDomain || currentUrl
      }handlers/UserProfileImageDownloadHandler.ashx?name=${name}`
    : name;
};

export const IsJsonString = (str) => {
  try {
    return JSON.parse(str);
  } catch (e) {
    return false;
  }
};

export const isValidUnixTimestamp = (value, type) => {
  // Check if the value is a valid number and within a reasonable timestamp range
  const isValid = !isNaN(value) && value > 0 && value < 2147483648000;

  if (isValid) {
    // If valid, convert to human-readable date and time
    let format = time.format.default;
    switch (type) {
      case "date":
        format = time.format.date;
        break;
      case "time":
        format = time.format.time;
        break;
    }
    const dateTime = moment.unix(value);

    return dateTime.format(format);
  } else {
    // If not valid, return the original value
    return value;
  }
};

export function addOrdinalSuffix(number) {
  const suffixes = ["th", "st", "nd", "rd"];
  const lastDigit = number % 10;

  // Special case for 11, 12, and 13 (they use 'th' suffix)
  const specialCase = number % 100 >= 11 && number % 100 <= 13;

  // Determine the suffix based on the last digit and special case
  const suffix = specialCase ? suffixes[0] : suffixes[lastDigit] || suffixes[0];

  return `${number}${suffix}`;
}

export const removeExtraCharacters = (text = "") => {
  return removeBackslashes(text.replace(/[\u200B-\u200D\uFEFF]/g, ""));
};

export const removeBackslashes = (str) => {
  return str.replace(/\\([^\w])/g, "$1");
};
export const clearAuthCookies = (crmOnly = false) => {
  removeFromCookies([
    "CRM-Auth",
    "companyKey",
    "companyId",
    "companyName",
    "smarthub-user",
    ...(crmOnly
      ? []
      : [".ASPXAUTH-Test", "PortalV2_Auth", "Auth", "DynamicMeta", "dbName"]),
  ]);
};

/**
 * Wipes client session data before company switch.
 * Clears all localStorage/sessionStorage and CRM auth cookies.
 * Legacy portal cookies (Auth, PortalV2_Auth) are kept — route-guard requires them in production.
 * Follow with window.location.href to /authorize so Redux in-memory state is also reset.
 */
export const clearSessionForCompanySwitch = () => {
  if (typeof window === "undefined") return;

  storage.clearAll();
  sessionStorage.clear();
  clearAuthCookies(true);
};

export const getLegacyCompanySelectorUrl = () =>
  legacyURL() + "Pages/CompanySelector.aspx?selection=true";

/** Clears CRM session then navigates to the legacy company selector (header switch flow). */
export const navigateToLegacyCompanySelector = () => {
  const url = getLegacyCompanySelectorUrl();
  clearSessionForCompanySwitch();
  window.location.href = url;
};

export const removePrefix = (input, delimiter = "$") => {
  const delimiterIndex = input?.indexOf(delimiter);
  return delimiterIndex !== -1
    ? input?.substring(delimiterIndex + 1)
    : (input ?? "");
};

export const emojiToUnicode = (text) => {
  return text
    .replace(/[\uD800-\uDBFF][\uDC00-\uDFFF]/g, function (character) {
      var charCode =
        (character.charCodeAt(0) - 0xd800) * 0x400 +
        character.charCodeAt(1) -
        0xdc00 +
        0x10000;
      return "&#" + charCode.toString(10).toUpperCase();
    })
    .replace(/\u2019/g, "&#8217;");
};

export const unicodeToEmoji = (text = "") =>
  text
    .replace(/&#[\d0-9]{6}/gi, (match) =>
      String.fromCodePoint(match.replace(/&#/g, ""))
    )
    .replace(/&#8217;/g, "\u2019");

export const unicodeToEmojiV2 = (text = "") => {
  if (text) {
    return text.replace(/\\u([\dA-Fa-f]{4})/g, (_) => {
      let cleanedString = _.replace(/\\\\/g, "\\");
      const emoji = JSON.parse(`"${cleanedString}"`);
      return emoji;
    });
  } else return text;
};

// Decode JSON-style escapes written as literal text (e.g. \u2014, \/)
// without JSON.parse on the full string — `/` and quotes in names must not break decoding.
export const decodeJsonUnicodeEscapes = (text) => {
  if (typeof text !== "string" || !text.includes("\\")) return text;
  return text
    .replace(/\\u([\dA-Fa-f]{4})/g, (_, hex) =>
      String.fromCharCode(parseInt(hex, 16))
    )
    .replace(/\\\//g, "/");
};

export const NameParser = ({
  firstName = "",
  lastName = "",
  displayName = "",
}) => {
  if (!firstName && !lastName && displayName) {
    let [fname, ...remaining] = displayName.split(" ");
    firstName = fname || "";
    lastName = remaining?.join(" ") || "";
  }

  return { firstName, lastName };
};

export const groupBy = (
  array = [],
  keyToGroup = "",
  subKey = "",
  handleNull = false
) => {
  return array.reduce((result, item) => {
    const key = subKey ? item[keyToGroup][subKey] : item[keyToGroup];
    if (key) {
      if (!result[key]) {
        result[key] = [];
      }
      result[key].push(item);
    } else {
      if (handleNull) {
        if (keyToGroup == "countryId") {
          if (!result["3"]) {
            result["3"] = [];
          }
          result["3"].push(item);
        } else if (keyToGroup == "stateProvinceId") {
          if (!result["201"]) {
            result["201"] = [];
          }
          result["201"].push(item);
        }
      }
    }
    return result;
  }, {});
};

export const removeLineBreak = (body = "") => {
  // remove \r\n to remove  extra space from top of email content
  return body?.replaceAll(/\r\n/g, "");
};

export const formatDateForGridFilters = (
  date,
  format = time.format.dateWithTime,
  endOfDay = false
) => {
  if (!date) return "";

  const m = moment(date);
  return (endOfDay ? m.endOf("day") : m).format(format);
};
export const GetDurationForAppointments = (
  count,
  duration,
  date = undefined
) => {
  let converted = moment(date).add(count, duration).format("YYYY-MM-DD");
  return converted;
};

export const fetchPaginatedData = async ({
  gqlQuery,
  dataKey,
  setData = () => console.log("no set data method passed"),
  dispatch = () => console.log("no dispatch method passed"),
  setLoading = () => {},
  setFetchingDone = () => {},
  take = 100,
  variables = {},
  maxCount = 0,
}) => {
  setLoading(true);
  let allItems = [];
  let totalCount = 0;
  let skip = 0;

  try {
    while (true) {
      const response = await gqlQuery.executeQuery({
        skip,
        take,
        ...variables,
      });
      const { data } = response;
      if (data?.[dataKey]?.items?.length) {
        allItems = allItems.concat(data[dataKey].items);
        totalCount = data[dataKey].totalCount;

        if (
          allItems.length >= totalCount ||
          (maxCount > 0 && allItems.length >= maxCount)
          // ||
          // allItems.length < totalCount
        ) {
          break;
        } else {
          skip += data?.[dataKey]?.items?.length;
        }
      } else {
        break;
      }
    }

    setData(allItems, totalCount);
    dispatch(allItems);
    setLoading(false);
    return allItems;
  } catch (err) {
    console.log(`Error fetching ${dataKey}:`, err);
    setData([]);
    dispatch([]);
  } finally {
    setLoading(false);
    setFetchingDone(true);
  }
};

// Get Initials of String //
export const GetInitialsFromString = (text = "") => {
  let initials = "";
  const parts = text?.split(" ").filter((str) => str);
  parts.forEach((p) => {
    initials += p[0].toUpperCase();
  });
  return initials;
};

// Method to convert date to the selected timezone and format it to ISO string
export const convertToTimezoneISO = (selectedTimezoneKey, selectedDate) => {
  if (!selectedTimezoneKey) {
    return momentTz.tz.guess();
  }
  // Use windows-iana to find the IANA timezones from the Windows timezone key
  const ianaZones = findIana(selectedTimezoneKey);

  if (!ianaZones || ianaZones.length === 0) {
    return momentTz.tz.guess();
    // throw new Error(`Timezone '${selectedTimezoneKey}' is not valid.`);
  }

  // Get the computer's current time zone
  const currentTimeZone = momentTz.tz.guess();

  // Find the matching IANA time zone
  const matchingZone = ianaZones.find((zone) => zone === currentTimeZone);

  // console.log(currentTimeZone,matchingZone, 'ianaTimezone');
  // Use the matching time zone or fall back to the first IANA zone
  const ianaTimezone = matchingZone || ianaZones[0];
  // console.log(ianaTimezone, 'ianaTimezone');

  //   // Convert the date to the selected IANA timezone
  //   const convertedDate = momentTz.tz(selectedDate, ianaTimezone);

  //   // Convert the date to ISO string format
  //   const isoString = convertedDate.format('YYYY-MM-DDTHH:mm:ss.SSSZ'); // 'Z' shows timezone offset instead of UTC

  //   return isoString;
  return ianaTimezone;
};

export const getTimezoneAbbreviation = (timezone) => {
  // If no timezone is provided, get the current timezone
  if (!timezone) {
    timezone = moment.tz.guess(); // Guess the current timezone
  }

  // Get the standard abbreviation for the timezone
  const abbreviation = moment.tz(timezone).format("z"); // 'z' gives the abbreviation

  // Return the standard abbreviation for the timezone
  return abbreviation;
};

export const getCurrentWindowsIanaTimezone = () => {
  // Get the current timezone in Windows format
  const currentWindowsTimezone = moment.tz.guess(); // This returns the IANA format

  // Convert it to IANA format
  const ianaTimezone = windowsToIana(currentWindowsTimezone);

  // If the conversion returns a valid timezone, return it
  return ianaTimezone ? ianaTimezone : currentWindowsTimezone; // Fallback to the guessed timezone
};

export const getOffset = (timezone) => {
  const regex = /\(UTC([+-]\d{2}:\d{2})\)/;
  const result = regex.exec(timezone);

  if (result) {
    const matchedOutput = result[1]; // This will give you just "+05:00"
    return matchedOutput;
  } else {
    console.log("No match found");
  }
};

export const getCurrentTimezone = (timezones) => {
  if (!Array.isArray(timezones) || timezones.length === 0) {
    return undefined;
  }

  // 1. Try to find timezone using windows-iana mapping
  try {
    const ianaZone =
      Intl.DateTimeFormat().resolvedOptions().timeZone || momentTz.tz.guess();
    if (ianaZone) {
      const windowsZones = findWindows(ianaZone);
      if (windowsZones && windowsZones.length > 0) {
        const matchedTz = timezones.find((tz) =>
          windowsZones.some(
            (wz) =>
              wz && tz.value && wz.toLowerCase() === tz.value.toLowerCase()
          )
        );
        if (matchedTz) {
          return matchedTz.value;
        }
      }
    }
  } catch (err) {
    console.error(
      "Error detecting current timezone via IANA/Windows mapping:",
      err
    );
  }

  // 2. Fallback to parsing local Date string (original logic)
  try {
    const dateStr = new Date().toString();
    const parts = dateStr.split("(");
    if (parts.length > 1) {
      const currZone = parts[1].split(")")[0];
      if (currZone) {
        const matchedTz = timezones.find(
          (tz) => tz.value && tz.value.toLowerCase() === currZone.toLowerCase()
        );
        if (matchedTz) {
          return matchedTz.value;
        }
      }
    }
  } catch (err) {
    console.error("Error detecting timezone via Date string:", err);
  }

  // 3. Fallback to offset matching (original logic)
  try {
    const offset = moment().utcOffset();
    const hours = Math.floor(Math.abs(offset) / 60);
    const minutes = Math.abs(offset) % 60;
    const sign = offset >= 0 ? "+" : "-";
    const formattedOffset = `(UTC${sign}${String(hours).padStart(2, "0")}:${String(minutes).padStart(2, "0")})`;
    const matchedTz = timezones.find(
      (tz) =>
        (tz.label && tz.label.startsWith(formattedOffset)) ||
        (tz.value && tz.value.startsWith(formattedOffset))
    );
    if (matchedTz) {
      return matchedTz.value;
    }
  } catch (err) {
    console.error("Error detecting timezone via offset:", err);
  }

  return timezones[0]?.value;
};

export const replacePlaceholderFromAuthProviderUrl = (providers = {}) => {
  let hostName = document?.location?.hostname || "";
  let placeholder = "{{additionalInfo}}";
  Object.keys(providers).map((key) => {
    if (providers[key].includes(placeholder)) {
      providers[key] = providers[key].replace(placeholder, hostName);
    }
  });
  return providers;
};

export const makeObjectFromArray = (arr = [], keyName = "", valueName = "") =>
  arr.reduce((obj, item) => {
    obj[item[keyName]] = item[valueName];
    return obj;
  }, {});

export const optoutContent =
  "<div style='text-align:center; margin-top:10px;color:#949494;font-weight:11px'>[[#OptOutEducatingText#]]</div>";
export const appendOptoutPlaceholderInEmail = (body = "") => {
  let trackingContent =
    '<img src="{{emailOpenUrl}}" alt="" width="1" height="1" style="display:none;" />';
  let customContent = ``;
  if (!body.includes("{{emailOpenUrl}}")) {
    customContent = customContent + `${trackingContent}`;
  }
  if (!body.includes("[[#OptOutEducatingText#]]")) {
    customContent = customContent + `${optoutContent}`;
  }

  let bodyEndIndex = body.indexOf("</body>");
  let content =
    body.slice(0, bodyEndIndex) + customContent + body.slice(bodyEndIndex);
  return bodyEndIndex !== -1 ? content : `${body}${customContent}`;
};

export const cleanupStandardTemplate = (html) => {
  return (
    html
      // remove <div> that contains OptOutEducatingText
      .replace(/<div[^>]*>[^<]*OptOutEducatingText[^<]*<\/div>/gi, "")
      // remove <img> that contains emailOpenUrl
      .replace(/<img[^>]*emailOpenUrl[^>]*>/gi, "")
      // remove br div tags
      .replace(/<div><br><\/div>/gi, "")
  );
};

export const cleanupAdvancedTemplate = (html) => {
  const parser = new DOMParser();
  const doc = parser.parseFromString(html, "text/html");

  // Find first <td>
  const td = doc.querySelector("td");

  return td ? td.innerHTML.trim() : "";
};

export const RemoveDuplicates = (arr = []) => {
  const unique = arr.filter(
    (obj, index, self) =>
      index === self.findIndex((o) => JSON.stringify(o) === JSON.stringify(obj))
  );

  return unique;
};

export const getPhoneValue = (phone, selected) => {
  if (!phone) return "";
  if (selected?.dial_code && String(phone).startsWith(selected.dial_code)) {
    return String(phone).slice(selected.dial_code.length);
  }
  return phone;
};

export const splitPrefillPhone = (rawPhone, countriesList = []) => {
  const fallback = { localNumber: "", countryCode: "+1", countryISOCode: "US" };
  const raw = String(rawPhone || "").trim();
  if (!raw) return fallback;

  const hasPlus = raw.includes("+");
  const digits = cleanedPhoneNumber(raw).replace(/\D/g, "");
  if (!digits) return fallback;

  const usFromNanp = {
    localNumber: digits.slice(1),
    countryCode: "+1",
    countryISOCode: "US",
  };

  // US first when there is no +: 10 digits = national, 11 starting with 1 = +1 + national
  if (!hasPlus) {
    if (digits.length === 10) {
      return { ...fallback, localNumber: digits };
    }
    if (digits.length === 11 && digits.startsWith("1")) {
      return usFromNanp;
    }
  }

  const list = Array.isArray(countriesList)
    ? countriesList
    : countriesList?.countries || [];
  const sorted = [...list].sort(
    (a, b) =>
      String(b?.dial_code || "").replace(/\D/g, "").length -
      String(a?.dial_code || "").replace(/\D/g, "").length
  );

  for (const country of sorted) {
    const dial = String(country?.dial_code || "").replace(/\s/g, "");
    const dialDigits = dial.replace(/\D/g, "");
    if (!dialDigits || !digits.startsWith(dialDigits)) continue;
    const local = digits.slice(dialDigits.length);
    if (local.length < 6) continue;
    return {
      localNumber: local,
      countryCode: dial.startsWith("+") ? dial : `+${dialDigits}`,
      countryISOCode: country.code || "US",
    };
  }

  if (digits.length === 11 && digits.startsWith("1")) {
    return usFromNanp;
  }

  return { ...fallback, localNumber: digits };
};

export const AllowOnlyAlphaNumaricSpace_Validation = (val) => {
  // Allow only A-Z, a-z, 0-9, underscore, space
  const regex = /^[A-Za-z0-9_ ]*$/;
  return regex.test(val);
};

export const ParseBool = (value) => {
  return String(value).toLowerCase() === "true";
};

export const calculateFutureDate = (groupType, futureLimit) => {
  const now = moment();
  console.log(groupType, futureLimit);

  if (groupType) {
    switch (groupType) {
      case GroupByEnums.minute:
        return now.add(futureLimit, "minutes");

      case GroupByEnums.hour:
        return now.add(futureLimit, "hours");

      case GroupByEnums.day:
        return now.add(futureLimit, "days");

      case GroupByEnums.week:
        return now.add(futureLimit, "weeks");

      case GroupByEnums.month:
        return now.add(futureLimit, "months");

      case GroupByEnums.year:
        return now.add(futureLimit, "years");

      default:
        throw new Error("Invalid group type");
    }
  } else return null;
};

/**
 * @name @getRandomPastelColor
 * @description Generates a random light pastel color in Hex format
 * @returns Hex color string
 */
export const getRandomPastelColor = () => {
  const h = Math.floor(Math.random() * 360);
  const s = Math.floor(Math.random() * 30) + 70; // 70-100% saturation for vibrancy
  const l = Math.floor(Math.random() * 10) + 85; // 85-95% lightness for pastel effect
  return hslToHex(h, s, l);
};

// Helper for HSL to Hex conversion
const hslToHex = (h, s, l) => {
  l /= 100;
  const a = (s * Math.min(l, 1 - l)) / 100;
  const f = (n) => {
    const k = (n + h / 30) % 12;
    const color = l - a * Math.max(Math.min(k - 3, 9 - k, 1), -1);
    return Math.round(255 * color)
      .toString(16)
      .padStart(2, "0");
  };
  return `#${f(0)}${f(8)}${f(4)}`;
};

export const GetAllowedHubsByFeatures = (featureKey, user = null) => {
  switch (featureKey) {
    case FeatureKeys.CRM:
      return RootHub.map((hub) => hub.hubName);

    case FeatureKeys.APPOINTMENT_SCHEDULING: {
      const allowed = [Report_Dashboard_signalr.hubName];
      if (user && user.RoleType === Enums.Roles.SchedulingTeamManager) {
        const rawFeatures = FeatureService.resolveFeatures(user);
        if (FeatureService.hasFeature(rawFeatures, FeatureKeys.CRM)) {
          allowed.push("leadhub");
        }
      }
      return allowed;
    }

    // for future features
    // case FeatureKeys.WORKFLOW:
    //   return [WF_signalr.hubName];
    default:
      return [];
  }
};

export const GenerateFiltersOptions = () => {
  return Object.keys(WidgetDurationEnums).map((key) => ({
    id: WidgetDurationEnums[key],
    title: key.split("_").join(" "),
  }));
};
